// src/screens/ChangePasswordScreen.js
import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  ActivityIndicator,
  ScrollView,
} from 'react-native';
import {
  getAuth,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from 'firebase/auth';
import Footer from '../components/Footer';
import { FORGOT_LINK_LABEL, setResetPrefill } from '../utils/passwordReset';

export default function ChangePasswordScreen({ navigation }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Please fill out all fields.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters long.');
      return;
    }

    setIsLoading(true);
    setError('');

    const auth = getAuth();
    const user = auth.currentUser;

    try {
      // Step 1: Re-authenticate the user for security
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Step 2: If re-authentication is successful, update the password
      await updatePassword(user, newPassword);

      Alert.alert('Success', 'Your password has been changed successfully.');
      if (navigation.canGoBack()) navigation.goBack(); else navigation.navigate('Lobby');
    } catch (e) {
      if (e.code === 'auth/wrong-password') {
        setError('Incorrect current password. Please try again.');
      } else {
        setError('An error occurred. Could not change your password.');
        console.error('Password change error:', e);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <View style={styles.outerContainer}>
      <ScrollView contentContainerStyle={styles.scrollContainer}>
        <Text style={styles.title}>Change Your Password</Text>
        <Text style={styles.subtitle}>
          Enter your current password and a new password below.
        </Text>

        {error ? <Text style={styles.errorText}>{error}</Text> : null}

        <TextInput
          style={styles.input}
          placeholder="Current Password"
          placeholderTextColor="#888"
          secureTextEntry
          value={currentPassword}
          onChangeText={setCurrentPassword}
        />
        {/* Forgot password (review FLOW-2): someone signed in with Google on
            an account that also has a password they never set (the R6
            password squat) has no current password to type here; the reset
            is the way to replace it. The address goes over in memory. */}
        <TouchableOpacity
          style={styles.forgotLink}
          onPress={() => { setResetPrefill(getAuth().currentUser?.email || ''); navigation.push('ForgotPassword'); }}
          accessibilityRole="link"
        >
          <Text style={styles.forgotText}>{FORGOT_LINK_LABEL}</Text>
        </TouchableOpacity>
        <TextInput
          style={styles.input}
          placeholder="New Password"
          placeholderTextColor="#888"
          secureTextEntry
          value={newPassword}
          onChangeText={setNewPassword}
        />
        <TextInput
          style={styles.input}
          placeholder="Confirm New Password"
          placeholderTextColor="#888"
          secureTextEntry
          value={confirmPassword}
          onChangeText={setConfirmPassword}
        />

        <TouchableOpacity
          style={styles.button}
          onPress={handleChangePassword}
          disabled={isLoading}
        >
          {isLoading ? (
            <ActivityIndicator color="#000" />
          ) : (
            <Text style={styles.buttonText}>Update Password</Text>
          )}
        </TouchableOpacity>

        <TouchableOpacity onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Lobby'))}>
          <Text style={styles.cancelLink}>Cancel</Text>
        </TouchableOpacity>
      </ScrollView>
      <Footer navigation={navigation} />
    </View>
  );
}

const styles = StyleSheet.create({
  outerContainer: { flex: 1, backgroundColor: '#000' },
  scrollContainer: { flexGrow: 1, justifyContent: 'center', alignItems: 'center', padding: 20 },
  title: { fontSize: 28, fontWeight: 'bold', color: '#FCD000', marginBottom: 10, textAlign: 'center' },
  subtitle: { fontSize: 16, color: '#CCC', textAlign: 'center', marginBottom: 30 },
  input: { width: '100%', backgroundColor: '#222', color: '#fff', padding: 15, marginBottom: 15, borderRadius: 8, fontSize: 16 },
  button: { backgroundColor: '#FCD000', width: '100%', padding: 15, borderRadius: 8, alignItems: 'center', marginTop: 10 },
  buttonText: { color: '#000', fontSize: 16, fontWeight: 'bold' },
  cancelLink: { color: '#888', marginTop: 25, fontSize: 14, textDecorationLine: 'underline' },
  forgotLink: { alignSelf: 'flex-end', minHeight: 44, paddingHorizontal: 8, justifyContent: 'center', marginTop: -10, marginBottom: 6 },
  forgotText: { color: '#FCD000', fontSize: 13, textDecorationLine: 'underline' },
  errorText: { color: 'salmon', marginBottom: 15, textAlign: 'center', fontSize: 16 },
});
